import './MainTabs.css'

import { IonIcon, IonLabel, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, useIonRouter } from '@ionic/react'
import { useT } from '@transifex/react'
import { apps, appsOutline, bag, bagOutline, list, listOutline, lockOpen, lockOpenOutline, pencil, pencilOutline, pieChart, pieChartOutline, refreshCircle, refreshCircleOutline } from 'ionicons/icons'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route } from 'react-router'

import MessagesModal from '../components/MessagesModal'
import { selectCustomer, selectSession } from '../store'
import Cluster from './Cluster'
import Deliveries from './Deliveries'
import OpenLock from './OpenLock'
import Rental from './Rental'
import Storage from './Storage'
import StorePickup from './StorePickup'

export const allTabs = [
  {
    tab: 'open-lock',
    href: '/tabs/open-lock',
    label: 'Open',
    icon: lockOpenOutline,
    iconActive: lockOpen,
  },
  {
    tab: 'registration',
    href: '/registration',
    label: 'Registration',
    icon: pencilOutline,
    iconActive: pencil,
  },
  {
    tab: 'deliveries',
    href: '/tabs/deliveries',
    label: 'Deliveries',
    icon: listOutline,
    iconActive: list,
  },
  {
    tab: 'storage',
    href: '/tabs/storage',
    label: 'Smart Storage',
    icon: pieChartOutline,
    iconActive: pieChart,
  },
  {
    tab: 'rental',
    href: '/tabs/rental',
    label: 'Rental',
    icon: refreshCircleOutline,
    iconActive: refreshCircle,
  },
  {
    tab: 'cluster',
    href: '/tabs/cluster',
    label: 'Parcel Locker',
    icon: appsOutline,
    iconActive: apps,
  },
  {
    tab: 'store-pickup',
    href: '/tabs/store-pickup',
    label: 'Click & Collect',
    icon: bagOutline,
    iconActive: bag,
  },
]

export default function MainTabs() {
  const { userType } = useSelector(selectSession)
  const customer = useSelector(selectCustomer)
  const t = useT()
  const router = useIonRouter()
  const [activeTab, setActiveTab] = useState(router.routeInfo.pathname === '/tabs' ? 'open-lock' : router.routeInfo.pathname.replace('/tabs/', ''))
  const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(true)

  const tabsToDisplay = useMemo(() => {
    if (!customer) return []

    const tabs: string[] = []

    const accesses = customer.lockaccesses.concat(customer.ownedlocks) || []

    let shouldShowOpenLock = true
    let shouldShowRegistration = false
    let shouldShowDeliveries = false
    let shouldShowRental = false
    let shouldShowStorage = false
    let shouldShowCluster = false
    let shouldShowStorePickup = false

    if (customer.customer.name === 'Gäst') {
      shouldShowRegistration = true
    }

    if (customer.customer.hasStorePickupBooking) {
      shouldShowStorePickup = true
    }

    let isStorePickupStaff: boolean | undefined = false
    let accessesWithMissingControllersCount = 0
    // eslint-disable-next-line no-restricted-syntax
    for (const access of accesses) {
      isStorePickupStaff = access.settings && access.settings.is_store_pickup_lock && access.users.some((u) => u.customer === customer.customer._id)

      if (isStorePickupStaff) {
        shouldShowStorePickup = true
      }

      if (access.sub_type === 'container') {
        shouldShowDeliveries = true
      }

      const isRentalUser = access.users.some((user) => user.customer === customer.customer._id && user.rental === true)

      if (isRentalUser) {
        shouldShowRental = true
      }

      if (access.storage) {
        shouldShowStorage = true
        if (access.controller === null && (access.controllers || []).length === 0) {
          accessesWithMissingControllersCount += 1
        }
      }

      const mayPerformClusterBooking = access.users.some((user) => user.customer === customer.customer._id && user.allowClusterBooking === true)

      if (mayPerformClusterBooking) {
        shouldShowCluster = true
      }
    }

    const isStorePickupClient = customer.customer.hasStorePickupBooking

    if (shouldShowStorePickup) {
      if (isStorePickupClient) {
        shouldShowOpenLock = customer.allAccesses.length > 0
      } else {
        shouldShowOpenLock = false
      }
    } else if (shouldShowStorage) {
      const allLocksMissingController = accessesWithMissingControllersCount === accesses.length
      const isRentalTabActive = router.routeInfo.pathname === '/tabs/rental'
      if (allLocksMissingController) {
        shouldShowOpenLock = false
        if (!isRentalTabActive) {
          router.push('/tabs/storage', 'none')
        }
      }
    }

    if (shouldShowOpenLock) tabs.push('open-lock')
    if (shouldShowRegistration) tabs.push('registration')
    if (shouldShowDeliveries) tabs.push('deliveries')
    if (shouldShowRental) tabs.push('rental')
    if (shouldShowStorage) tabs.push('storage')
    if (shouldShowCluster) tabs.push('cluster')
    if (shouldShowStorePickup) {
      tabs.push('store-pickup')
      if (activeTab !== 'store-pickup' && activeTab !== 'registration') {
        if (isStorePickupStaff) {
          router.push('/tabs/store-pickup', 'none')
        } else if (isStorePickupClient && customer.allAccesses.length === 0) {
          router.push('/tabs/store-pickup', 'none')
        }
      }
    }

    return allTabs.map(({ tab, href, icon, iconActive, label }, i) => {
      if (!tabs.includes(tab)) return null

      const isTabActive = activeTab === tab || (i === 0 && router.routeInfo.pathname === '/tabs')

      return (
        <IonTabButton selected={isTabActive} key={tab} tab={tab} href={href}>
          <IonIcon color="primary" icon={isTabActive ? iconActive : icon} />
          <IonLabel color={isTabActive ? 'primary' : ''}>{t(label)}</IonLabel>
        </IonTabButton>
      )
    })
  }, [customer, activeTab])

  useEffect(() => {
    const tab = router.routeInfo.pathname.replace('/tabs/', '')

    if (tab !== activeTab) {
      setActiveTab(tab)
    }
  }, [router.routeInfo.pathname])

  return (
    <>
      <IonTabs>
        <IonRouterOutlet id="tabs">
          <Route path="/tabs/open-lock" render={() => <OpenLock />} exact />
          <Redirect from="/tabs/registration" to="/registration" exact />
          <Route path="/tabs/deliveries" render={() => <Deliveries />} exact />
          <Route path="/tabs/storage" render={() => <Storage />} exact />
          <Route path="/tabs/rental" render={() => <Rental />} exact />
          <Route path="/tabs/cluster" render={() => <Cluster />} exact />
          <Route path="/tabs/store-pickup" render={() => <StorePickup />} exact />
          <Redirect from="/tabs" to="/tabs/open-lock" exact />
          <Redirect from="/" to="/tabs/open-lock" exact />
        </IonRouterOutlet>
        <IonTabBar color="light" className={userType ? 'visible' : ''} hidden={!userType} slot="bottom">
          {tabsToDisplay}
        </IonTabBar>
      </IonTabs>
      {userType === 'customer' && isMessagesModalOpen && <MessagesModal onDismiss={() => setIsMessagesModalOpen(false)} />}
    </>
  )
}
